import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, Select, Table, Tag } from 'antd';
import styled from 'styled-components';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import {
	TableStyles
} from '~/modules/components/share-feedback/share-feedback-report/share-feedback-report.css';
import { Label } from '~/pages/common.css';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import WebhookEndpointService from '~/modules/services/webhook-endpoint-service';
import WebhookSecretRevealModal from './webhook-secret-reveal-modal';
import WebhookDeliveriesModal from './webhook-deliveries-modal';
import { captureError } from '../../../pixwel/exception-handler.factory';

const QUERY_KEY = ['webhookEndpoints'];
const EVENT_TYPES_KEY = ['webhookEventTypes'];

const WebhooksContainer = styled.div`
	min-height: fit-content;

	fieldset {
		margin-bottom: 2rem;
	}

	.warning-copy {
		font-size: 0.85rem;
		color: ${({ theme }) => theme['silver-base']};
		margin-bottom: 1rem;
	}

	.create-form {
		display: flex;
		gap: 0.75rem;
		align-items: flex-end;
		margin-bottom: 1.5rem;
		flex-wrap: wrap;
	}

	.create-form > div {
		display: flex;
		flex-direction: column;
	}

	.create-form .url-field {
		flex: 1 1 22rem;
	}

	.create-form .events-field {
		flex: 1 1 18rem;
	}
`;

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

const Webhooks = () => {
	const queryClient = useQueryClient();

	const [url, setUrl] = useState('');
	const [description, setDescription] = useState('');
	const [selectedEvents, setSelectedEvents] = useState(['*']);
	const [revealedSecret, setRevealedSecret] = useState(null);
	const [deliveriesEndpoint, setDeliveriesEndpoint] = useState(null);

	const { data: endpoints = [], isLoading } = useQuery(
		QUERY_KEY,
		() => WebhookEndpointService.list(),
		{ refetchOnWindowFocus: false }
	);

	const { data: eventTypes = [] } = useQuery(
		EVENT_TYPES_KEY,
		() => WebhookEndpointService.eventTypes(),
		{ refetchOnWindowFocus: false, staleTime: Infinity }
	);

	const eventOptions = [
		{ label: 'All events (*)', value: '*' },
		...eventTypes.map((name) => ({ label: name, value: name }))
	];

	const { mutateAsync: createEndpoint, isLoading: isCreating } = useMutation(
		(payload) => WebhookEndpointService.create(payload),
		{
			onSuccess: (endpoint) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setRevealedSecret(endpoint.secret);
				setUrl('');
				setDescription('');
				setSelectedEvents(['*']);
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || err.message || 'Failed to create endpoint');
				captureError(err, `Error creating webhook endpoint: ${err.message}`);
			}
		}
	);

	const { mutateAsync: removeEndpoint } = useMutation(
		(id) => WebhookEndpointService.remove(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEY);
				ToastService.create('success', 'Webhook endpoint deleted');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to delete endpoint');
				captureError(err, `Error deleting webhook endpoint: ${err.message}`);
			}
		}
	);

	const { mutateAsync: toggleStatus } = useMutation(
		({ id, status }) => WebhookEndpointService.update(id, { status }),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEY);
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to update endpoint');
				captureError(err, `Error updating webhook endpoint: ${err.message}`);
			}
		}
	);

	const { mutateAsync: rollSecret } = useMutation(
		(id) => WebhookEndpointService.rollSecret(id),
		{
			onSuccess: (endpoint) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setRevealedSecret(endpoint.secret);
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to roll secret');
				captureError(err, `Error rolling webhook secret: ${err.message}`);
			}
		}
	);

	const handleCreate = async() => {
		const trimmed = url.trim();
		if(!trimmed) {
			return;
		}
		await createEndpoint({
			url: trimmed,
			enabledEvents: selectedEvents.length ? selectedEvents : ['*'],
			description: description.trim()
		});
	};

	const handleDelete = async(row) => {
		const ok = await confirm({
			title: 'Delete webhook endpoint',
			content: `Delete "${row.url}"? Deliveries to this endpoint will stop immediately.`,
			confirmText: 'Delete',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		await removeEndpoint(row._id);
	};

	const handleRollSecret = async(row) => {
		const ok = await confirm({
			title: 'Roll signing secret',
			content: 'Generate a new signing secret? The current secret will stop working immediately.',
			confirmText: 'Roll secret',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		await rollSecret(row._id);
	};

	const columns = [
		{ title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true },
		{
			title: 'Events',
			dataIndex: 'enabledEvents',
			key: 'enabledEvents',
			render: (events = []) => (events.includes('*') ? <Tag>all</Tag> : `${events.length} selected`)
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status) => <Tag color={status === 'enabled' ? 'green' : 'red'}>{status}</Tag>
		},
		{ title: 'Last delivery', dataIndex: 'lastSuccessAt', key: 'lastSuccessAt', render: formatDate },
		{
			title: 'Action',
			key: 'action',
			width: 320,
			render: (_value, row) => (
				<div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
					<Button
						variant="secondary"
						data-testid="webhook-deliveries-button"
						onClick={() => setDeliveriesEndpoint(row)}
					>
						Deliveries
					</Button>
					<Button
						variant="secondary"
						data-testid="webhook-toggle-button"
						onClick={() => toggleStatus({
							id: row._id,
							status: row.status === 'enabled' ? 'disabled' : 'enabled'
						})}
					>
						{row.status === 'enabled' ? 'Disable' : 'Enable'}
					</Button>
					<Button
						variant="secondary"
						data-testid="webhook-roll-secret-button"
						onClick={() => handleRollSecret(row)}
					>
						Roll secret
					</Button>
					<Button
						variant="danger"
						data-testid="webhook-delete-button"
						onClick={() => handleDelete(row)}
					>
						<Icon use="trash" color="white" /> Delete
					</Button>
				</div>
			)
		}
	];

	return (
		<WebhooksContainer data-testid="preferences-webhooks">
			<fieldset>
				<legend className="h5">Create webhook endpoint</legend>
				<p className="warning-copy">
					Pixwel will POST a signed JSON payload to this URL when events you can see occur. You only
					receive events for resources your account has access to. The signing secret is shown once,
					when the endpoint is created.
				</p>
				<div className="create-form">
					<div className="url-field">
						<Label>Endpoint URL</Label>
						<Input
							placeholder="https://example.com/pixwel/webhooks"
							value={url}
							onChange={(e) => setUrl(e.target.value)}
							data-testid="webhook-url-input"
						/>
					</div>
					<div className="events-field">
						<Label>Events</Label>
						<Select
							mode="multiple"
							allowClear
							style={{ width: '100%' }}
							placeholder="All events"
							value={selectedEvents}
							onChange={setSelectedEvents}
							options={eventOptions}
							data-testid="webhook-events-select"
						/>
					</div>
					<div className="url-field">
						<Label>Description (optional)</Label>
						<Input
							placeholder="e.g. production integration"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							data-testid="webhook-description-input"
						/>
					</div>
					<div>
						<Button
							onClick={handleCreate}
							loading={isCreating}
							disabled={!url.trim() || isCreating}
							data-testid="webhook-create-button"
						>
							Create endpoint
						</Button>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<legend className="h5">Endpoints</legend>
				<TableStyles>
					<Table
						columns={columns}
						dataSource={endpoints.map((e) => ({ ...e, key: e._id }))}
						pagination={false}
						loading={isLoading}
						locale={{ emptyText: <EmptyState message="No webhook endpoints" /> }}
					/>
				</TableStyles>
			</fieldset>
			<WebhookSecretRevealModal
				secret={revealedSecret}
				onClose={() => setRevealedSecret(null)}
			/>
			<WebhookDeliveriesModal
				endpoint={deliveriesEndpoint}
				onClose={() => setDeliveriesEndpoint(null)}
			/>
		</WebhooksContainer>
	);
};

export default Webhooks;
